'use client';

import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Autocomplete,
  type AutocompleteFieldProps,
  type AutocompleteOption,
} from '@kit/ui/autocomplete';

import { getAllCountryOptions } from '../../lib/countries';

interface NationalityPickerProps extends AutocompleteFieldProps {
  /** Selected ISO 3166-1 alpha-2 country codes. */
  value: string[];
  onChange: (codes: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  /**
   * Optional context slot rendered above the search field (desktop) and above
   * the sheet search input (mobile). Forwarded verbatim to the primitive.
   */
  header?: ReactNode;
  /**
   * Empty-result text. Defaults to the nationality wording; a surface that
   * reuses the picker for another country list passes its own.
   */
  emptyMessage?: string;
}

/**
 * Multi-country picker for nationalities. Thin wrapper over the canonical
 * `@kit/ui/autocomplete` primitive in `multiple` mode (type-to-filter inline,
 * no separate search box). The primitive renders each selection as a removable
 * chip inside the field, so this wrapper adds no separate badge strip. Sources
 * the full ISO 3166-1 list (+ flags) from `getAllCountryOptions`.
 *
 * Canonical home (`@kit/artists`) — the package that owns the country dataset.
 * Consumed by the dancer profile wizard/edit, contract details, and the
 * competition application form.
 */
export function NationalityPicker({
  value,
  onChange,
  disabled = false,
  placeholder,
  header,
  emptyMessage,
  'aria-label': ariaLabel,
  ...fieldProps
}: NationalityPickerProps) {
  const { t } = useTranslation();

  const countries = useMemo(() => getAllCountryOptions(), []);

  const options = useMemo<AutocompleteOption[]>(
    () =>
      countries.map((country) => ({
        value: country.country_code,
        label: `${country.country_name} (${country.country_code})`,
        icon: <span>{country.flag}</span>,
      })),
    [countries],
  );

  return (
    <Autocomplete
      {...fieldProps}
      multiple
      clearOnSelect
      value={value ?? []}
      onChange={(next) => onChange(Array.isArray(next) ? next : [])}
      options={options}
      disabled={disabled}
      header={header}
      placeholder={placeholder ?? t('dancer:nationalityPicker.addPlaceholder')}
      emptyMessage={emptyMessage ?? t('dancer:nationalityPicker.noResults')}
      aria-label={ariaLabel ?? t('dancer:nationalityPicker.searchPlaceholder')}
    />
  );
}
